import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Autocomplete,
  Avatar,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { UserToOrganization } from '../types';

type Props = {
  users: UserToOrganization[];
  value?: UserToOrganization | null;
  onSelectUser: (uuid: string | null) => void;
  loading: boolean;
  loggedUserId: string;
};

const getPhotoUrl = (user: UserToOrganization['user']) => user?.photo_url || '';
const getCompanyName = (option: UserToOrganization) => option.user.company?.name || '';

// Componente Avatar com lazy loading
const LazyAvatar: React.FC<{ user: UserToOrganization['user']; userId: string }> = ({
  user,
  userId,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (avatarRef.current) {
      observer.observe(avatarRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const photoUrl = getPhotoUrl(user);

  return (
    <Avatar
      ref={avatarRef}
      sx={{ width: 32, height: 32 }}
      src={isVisible && photoUrl && !imageError ? photoUrl : undefined}
      alt={user.first_name}
      onLoad={handleImageLoad}
      onError={handleImageError}
    >
      {user.first_name?.[0]}
    </Avatar>
  );
};

const SearchBar: React.FC<Props> = ({ users, value, onSelectUser, loading }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const nameA = `${a.user.first_name} ${a.user.last_name}`.toLowerCase();
      const nameB = `${b.user.first_name} ${b.user.last_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  const filterOptions = (options: UserToOrganization[], { inputValue }: any) => {
    const search = inputValue.toLowerCase();
    return options.filter(option => {
      const fullName = `${option.user.first_name} ${option.user.last_name}`.toLowerCase();
      const company = getCompanyName(option).toLowerCase();
      return fullName.includes(search) || company.includes(search);
    });
  };

  return (
    <Autocomplete
      value={value || null}
      sx={{ width: '100%' }}
      options={sortedUsers}
      getOptionLabel={option => {
        const company = getCompanyName(option);
        const lastName = option.user.last_name.split(' ').slice(-1)[0];
        return company
          ? `${option.user.first_name} ${lastName} | ${company}`.trim()
          : `${option.user.first_name} ${lastName}`;
      }}
      filterOptions={filterOptions}
      onChange={(_, value) => onSelectUser(value ? value.user.id : null)}
      loading={loading}
      noOptionsText="Nenhum membro encontrado."
      renderOption={(props, option) => (
        <ListItem
          {...props}
          key={option.user.id}
          alignItems="flex-start"
          sx={{ gap: 0, padding: 0 }}
        >
          <ListItemAvatar>
            <LazyAvatar user={option.user} userId={option.user.id} />
          </ListItemAvatar>
          <ListItemText
            primary={`${option.user.first_name} ${option.user.last_name.split(' ').slice(-1)[0]}`}
            secondary={getCompanyName(option)}
            primaryTypographyProps={{ fontWeight: 500, fontSize: 10 }}
            secondaryTypographyProps={{ color: 'text.secondary', fontSize: 8 }}
          />
        </ListItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label="Buscar membro"
          variant="outlined"
          sx={{ width: isMobile ? '100%' : '40%' }}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment && <span>{params.InputProps.endAdornment}</span>}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchBar;
